/**
 * Formateadores y etiquetas de presentación de Flotas (US-2.3, es-CO).
 *
 * Los estados del semáforo (color + icono + texto) viven en los badges de la feature;
 * acá solo se traducen enums del contrato a texto visible y se formatea moneda/fecha.
 */
import type {
  CustomerDocumentType,
  FleetAuthorizationStatus,
  FleetBillingMode,
  FleetStatus,
} from '@goelectric/shared-types';

export const FLEET_DOCUMENT_TYPE_LABELS: Record<CustomerDocumentType, string> = {
  CC: 'Cédula de ciudadanía',
  CE: 'Cédula de extranjería',
  NIT: 'NIT',
  PPT: 'Permiso por Protección Temporal',
};

export const FLEET_STATUS_LABELS: Record<FleetStatus, string> = {
  ACTIVE: 'Activa',
  INACTIVE: 'Inactiva',
};

export const FLEET_VEHICLE_STATUS_LABELS: Record<FleetStatus, string> = {
  ACTIVE: 'Activo',
  INACTIVE: 'Inactivo',
};

export const FLEET_BILLING_MODE_LABELS: Record<FleetBillingMode, string> = {
  UNIFIED: 'Unificada',
};

export const FLEET_AUTHORIZATION_STATUS_LABELS: Record<FleetAuthorizationStatus, string> = {
  ACTIVE: 'Autorizado',
  BLOCKED: 'Bloqueado',
  PARTIAL: 'Parcial',
  ERROR: 'Error de autorización',
};

/**
 * Formatea un monto en pesos colombianos (COP, sin decimales). El contrato expresa
 * los montos en COP; se muestra con el formato local (`$ 18.500`).
 */
export function formatCop(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return '—';
  }
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(amount);
}

/** Fecha/hora corta local (es-CO) para el detalle. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Documento fiscal de la flota para presentación. La flota es una empresa, así que el
 * caso normal es NIT; se conserva el tipo para no inventar un DV que el contrato no expone.
 */
export function formatFleetDocument(fleet: {
  documentType?: CustomerDocumentType;
  documentNumber?: string;
}): string {
  if (!fleet.documentNumber) {
    return '—';
  }
  if (!fleet.documentType || fleet.documentType === 'NIT') {
    return fleet.documentNumber;
  }
  return `${FLEET_DOCUMENT_TYPE_LABELS[fleet.documentType]} ${fleet.documentNumber}`;
}
